import type {
  GameMode,
  RoomBrowserEntry,
  RoomConfig,
  RoomDetails,
  RoomPlayerSummary,
} from "@mmotdg/shared";
import {
  ApiError,
  createLobbyRoom,
  getMyLobbyRoom,
  joinLobbyRoom,
  kickLobbyPlayer,
  leaveLobbyRoom,
  listLobbyRooms,
  setLobbyReady,
  startLobbyRoom,
} from "../api/AuthClient.js";
import { appState } from "../state/AppState.js";
import { icon } from "./icons.js";

type FilterMode = "all" | "arena" | "coop" | "boss_hunt" | "boss_endurance";

let browserPollHandle: number | null = null;
let roomPollHandle: number | null = null;
let currentFilter: FilterMode = "all";
let cachedRooms: RoomBrowserEntry[] = [];

const STARTABLE_MODES: GameMode[] = ["arena", "coop", "boss_hunt", "boss_endurance"];

export function renderRoomBrowserScreen(container: HTMLElement): void {
  stopBrowserPolling();
  stopRoomPolling();

  container.innerHTML = `
    <div class="top-bar">
      <div class="brand">Wardlines</div>
      <div class="user">
        <button id="back-to-lobby" type="button" style="background: transparent; border: 1px solid var(--line-strong);">
          <span style="display:inline-flex;align-items:center;gap:6px;">${icon("close", 14)} Back</span>
        </button>
      </div>
    </div>
    <div class="lobby-main">
      <div class="tab-content">
        <div class="panel" style="max-width: 1000px; margin: 0 auto;">
          <div style="display:flex; align-items:center; justify-content:space-between; gap: 16px; flex-wrap: wrap;">
            <div>
              <h2 class="panel-title" style="margin-bottom: 4px;">Room Browser</h2>
              <p class="panel-subtitle">Browse open rooms or host your own match.</p>
            </div>
            <div style="display:flex; gap: 8px;">
              <button id="refresh-rooms" type="button" style="background: transparent; border: 1px solid var(--line-strong);">
                <span style="display:inline-flex;align-items:center;gap:6px;">${icon("refresh", 14)} Refresh</span>
              </button>
              <button id="create-room" type="button" class="primary">
                <span style="display:inline-flex;align-items:center;gap:6px;">${icon("plus", 14)} Create room</span>
              </button>
            </div>
          </div>

          <div id="room-filters" style="display:flex; gap: 6px; margin: 18px 0 12px 0; flex-wrap: wrap;">
            ${renderFilterChips()}
          </div>

          <div id="rooms-table">${renderTablePlaceholder()}</div>
        </div>
      </div>
    </div>
  `;

  container.querySelector<HTMLButtonElement>("#back-to-lobby")!.addEventListener("click", () => {
    appState.set({ screen: "lobby" });
  });
  container.querySelector<HTMLButtonElement>("#refresh-rooms")!.addEventListener("click", () => {
    void refreshRooms(container);
  });
  container.querySelector<HTMLButtonElement>("#create-room")!.addEventListener("click", () => {
    openCreateRoomModal();
  });

  attachFilterHandlers(container);

  void refreshRooms(container);
  browserPollHandle = window.setInterval(() => void refreshRooms(container), 4000);
}

function renderFilterChips(): string {
  const filters: Array<{ key: FilterMode; label: string }> = [
    { key: "all", label: "All" },
    { key: "arena", label: "Arena" },
    { key: "coop", label: "Coop" },
    { key: "boss_hunt", label: "Boss Hunt" },
    { key: "boss_endurance", label: "Boss Endurance" },
  ];
  return filters
    .map(
      (f) => `
      <button type="button" class="filter-chip ${currentFilter === f.key ? "active" : ""}" data-filter="${f.key}">
        ${escapeHtml(f.label)}
      </button>`,
    )
    .join("");
}

function attachFilterHandlers(container: HTMLElement): void {
  container.querySelectorAll<HTMLButtonElement>(".filter-chip").forEach((btn) => {
    btn.addEventListener("click", () => {
      currentFilter = btn.dataset.filter as FilterMode;
      const filterArea = container.querySelector<HTMLDivElement>("#room-filters");
      if (filterArea) filterArea.innerHTML = renderFilterChips();
      attachFilterHandlers(container);
      renderRoomsTable(container);
    });
  });
}

async function refreshRooms(container: HTMLElement): Promise<void> {
  try {
    const { rooms } = await listLobbyRooms();
    cachedRooms = rooms;
    renderRoomsTable(container);
  } catch {
    const target = container.querySelector<HTMLDivElement>("#rooms-table");
    if (target) target.innerHTML = `<div class="empty">Failed to load rooms.</div>`;
  }
}

function renderTablePlaceholder(): string {
  return `<div class="empty">Loading rooms...</div>`;
}

function renderRoomsTable(container: HTMLElement): void {
  const target = container.querySelector<HTMLDivElement>("#rooms-table");
  if (!target) return;
  const filtered = cachedRooms.filter((r) => currentFilter === "all" || r.config.mode === currentFilter);
  if (filtered.length === 0) {
    target.innerHTML = `<div class="empty">No rooms match this filter. Be the first to host one.</div>`;
    return;
  }
  target.innerHTML = `
    <div class="room-table">
      <div class="room-table-head">
        <div>Mode</div>
        <div>Boss</div>
        <div>Players</div>
        <div>Stake</div>
        <div>Pot</div>
        <div>Status</div>
        <div></div>
      </div>
      ${filtered.map(renderRoomRow).join("")}
    </div>
  `;
  target.querySelectorAll<HTMLButtonElement>("button.join-room").forEach((btn) => {
    btn.addEventListener("click", () => {
      const roomId = btn.dataset.roomId!;
      void joinRoom(roomId);
    });
  });
}

function renderRoomRow(room: RoomBrowserEntry): string {
  const bossLabel =
    room.config.bossId && room.config.bossDifficulty
      ? `${escapeHtml(room.config.bossId)} · ${escapeHtml(room.config.bossDifficulty)}`
      : "—";
  const stakeLabel = room.config.freePlay ? "Free" : formatShards(room.config.stake);
  const potLabel = room.config.freePlay ? "—" : formatShards(room.pot);
  const canJoin = room.status === "waiting" && room.playerCount < room.config.capacity;
  const action = canJoin
    ? `<button type="button" class="primary join-room" data-room-id="${room.id}">Join</button>`
    : `<button type="button" class="join-room" data-room-id="${room.id}" disabled style="opacity: 0.5;">${room.status === "waiting" ? "Full" : "Started"}</button>`;
  return `
    <div class="room-row">
      <div><strong>${escapeHtml(modeLabel(room.config.mode))}</strong></div>
      <div class="muted">${bossLabel}</div>
      <div>${room.playerCount}/${room.config.capacity}</div>
      <div>${stakeLabel}</div>
      <div>${potLabel}</div>
      <div><span class="status-pill status-${room.status}">${escapeHtml(room.status.replace("_", " "))}</span></div>
      <div style="text-align: right;">${action}</div>
    </div>
  `;
}

async function joinRoom(roomId: string): Promise<void> {
  try {
    const { room } = await joinLobbyRoom(roomId);
    enterRoomScreen(room);
  } catch (err) {
    showToast(extractErrorCode(err, "JOIN_FAILED"));
  }
}

function enterRoomScreen(room: RoomDetails): void {
  appState.set({ screen: "room", currentRoomId: room.id });
}

function openCreateRoomModal(): void {
  const overlay = document.createElement("div");
  overlay.className = "screen modal-overlay";
  overlay.style.background = "rgba(7, 9, 15, 0.85)";
  overlay.style.zIndex = "100";
  overlay.innerHTML = `
    <div class="panel" style="min-width: 420px; max-width: 520px;">
      <h2 class="panel-title">Create room</h2>
      <p class="panel-subtitle">Set the mode, capacity, and stake.</p>

      <div class="form-row">
        <label>Mode</label>
        <select id="cr-mode">
          <option value="arena">Arena (1v1)</option>
          <option value="coop">Coop Survival</option>
          <option value="boss_hunt">Boss Hunt</option>
          <option value="boss_endurance">Boss Endurance</option>
        </select>
      </div>

      <div class="form-row" id="cr-boss-row" style="display:none; flex-direction:column; gap:8px;">
        <div style="display:flex; gap:8px;">
          <label style="flex:1;">
            <span style="display:block; margin-bottom:4px;">Boss</span>
            <select id="cr-boss-id">
              <option value="icosahedron">Icosahedron</option>
            </select>
          </label>
          <label style="flex:1;">
            <span style="display:block; margin-bottom:4px;">Difficulty</span>
            <select id="cr-boss-diff">
              <option value="normal">Normal</option>
              <option value="heroic">Heroic</option>
              <option value="nightmare">Nightmare</option>
            </select>
          </label>
        </div>
      </div>

      <div class="form-row">
        <label>Capacity</label>
        <input id="cr-capacity" type="number" min="2" max="4" value="2" />
      </div>

      <div class="form-row">
        <label style="display:flex; gap: 8px; align-items: center;">
          <input id="cr-free" type="checkbox" /> Free play (no stake)
        </label>
      </div>

      <div class="form-row" id="cr-stake-row">
        <label>Stake (shards)</label>
        <input id="cr-stake" type="number" min="0" step="10" value="0" />
      </div>

      <div class="form-row">
        <label style="display:flex; gap: 8px; align-items: center;">
          <input id="cr-private" type="checkbox" /> Private room (invite code)
        </label>
      </div>

      <div class="error-text" id="cr-error"></div>

      <div class="btn-row spaced">
        <button id="cr-cancel" type="button" style="background: transparent; border: 1px solid var(--line-strong);">Cancel</button>
        <button id="cr-submit" class="primary" type="button">Create</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const modeSel = overlay.querySelector<HTMLSelectElement>("#cr-mode")!;
  const capInput = overlay.querySelector<HTMLInputElement>("#cr-capacity")!;
  const freeChk = overlay.querySelector<HTMLInputElement>("#cr-free")!;
  const stakeRow = overlay.querySelector<HTMLDivElement>("#cr-stake-row")!;
  const stakeInput = overlay.querySelector<HTMLInputElement>("#cr-stake")!;
  const privateChk = overlay.querySelector<HTMLInputElement>("#cr-private")!;
  const errSlot = overlay.querySelector<HTMLDivElement>("#cr-error")!;
  const bossRow = overlay.querySelector<HTMLDivElement>("#cr-boss-row")!;
  const bossIdSel = overlay.querySelector<HTMLSelectElement>("#cr-boss-id")!;
  const bossDiffSel = overlay.querySelector<HTMLSelectElement>("#cr-boss-diff")!;

  modeSel.addEventListener("change", () => {
    const isBoss = modeSel.value === "boss_hunt" || modeSel.value === "boss_endurance";
    bossRow.style.display = isBoss ? "flex" : "none";
    if (modeSel.value === "arena") {
      capInput.value = "2";
      capInput.min = "2";
      capInput.max = "2";
      capInput.disabled = true;
    } else if (isBoss) {
      capInput.disabled = false;
      capInput.min = "2";
      capInput.max = "6";
      if (Number(capInput.value) < 2) capInput.value = "2";
      if (Number(capInput.value) > 6) capInput.value = "6";
    } else {
      capInput.disabled = false;
      capInput.min = "2";
      capInput.max = "4";
      if (Number(capInput.value) > 4) capInput.value = "4";
    }
  });
  modeSel.dispatchEvent(new Event("change"));

  freeChk.addEventListener("change", () => {
    stakeRow.style.display = freeChk.checked ? "none" : "flex";
    if (freeChk.checked) stakeInput.value = "0";
  });

  overlay.querySelector<HTMLButtonElement>("#cr-cancel")!.addEventListener("click", () => overlay.remove());
  overlay.querySelector<HTMLButtonElement>("#cr-submit")!.addEventListener("click", async () => {
    const stake = freeChk.checked ? 0 : Math.max(0, Math.floor(Number(stakeInput.value) || 0));
    const mode = modeSel.value as GameMode;
    const isBoss = mode === "boss_hunt" || mode === "boss_endurance";
    const maxCap = isBoss ? 6 : mode === "arena" ? 2 : 4;
    const config: RoomConfig = {
      mode,
      capacity: Math.max(2, Math.min(maxCap, Math.floor(Number(capInput.value) || 2))),
      bossId: isBoss ? bossIdSel.value : null,
      bossDifficulty: isBoss ? (bossDiffSel.value as "normal" | "heroic" | "nightmare") : null,
      bossVariant: isBoss ? (mode === "boss_hunt" ? "hunt" : "endurance") : null,
      stake,
      freePlay: freeChk.checked || stake === 0,
      isPrivate: privateChk.checked,
    };
    errSlot.textContent = "";
    const submitBtn = overlay.querySelector<HTMLButtonElement>("#cr-submit")!;
    submitBtn.disabled = true;
    try {
      const { room } = await createLobbyRoom(config);
      overlay.remove();
      enterRoomScreen(room);
    } catch (err) {
      submitBtn.disabled = false;
      errSlot.textContent = humanError(extractErrorCode(err, "CREATE_FAILED"));
    }
  });
}

export function renderRoomLobbyScreen(container: HTMLElement): void {
  stopBrowserPolling();
  stopRoomPolling();
  container.innerHTML = `
    <div class="top-bar">
      <div class="brand">Wardlines</div>
      <div class="user">
        <button id="leave-room" type="button" class="danger">
          <span style="display:inline-flex;align-items:center;gap:6px;">${icon("close", 14)} Leave room</span>
        </button>
      </div>
    </div>
    <div class="lobby-main">
      <div class="tab-content">
        <div id="room-content" style="max-width: 760px; margin: 0 auto;">
          <div class="panel">Loading room...</div>
        </div>
      </div>
    </div>
  `;

  container.querySelector<HTMLButtonElement>("#leave-room")!.addEventListener("click", () => {
    void leaveRoom();
  });

  void refreshRoom(container);
  roomPollHandle = window.setInterval(() => void refreshRoom(container), 1500);
}

async function refreshRoom(container: HTMLElement): Promise<void> {
  try {
    const { room } = await getMyLobbyRoom();
    if (!room) {
      stopRoomPolling();
      showToast("You left the room.");
      appState.set({ screen: "room_browser", currentRoomId: null });
      return;
    }
    if (room.status === "in_progress" && room.assignment) {
      stopRoomPolling();
      appState.set({
        screen: "match",
        matchAssignment: {
          roomId: room.id,
          mode: room.config.mode,
          serverId: room.assignment.serverId,
          serverHost: room.assignment.serverHost,
          serverPort: room.assignment.serverPort,
          tutorialLevel: null,
        },
        currentRoomId: null,
      });
      return;
    }
    renderRoomDetail(container, room);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      stopRoomPolling();
      appState.set({ screen: "room_browser", currentRoomId: null });
    }
  }
}

function renderRoomDetail(container: HTMLElement, room: RoomDetails): void {
  const target = container.querySelector<HTMLDivElement>("#room-content");
  if (!target) return;
  const user = appState.get().user;
  if (!user) return;
  const isHost = room.hostUserId === user.id;
  const me = room.players.find((p) => p.userId === user.id);
  const everyoneReady = room.players.length >= 2 && room.players.every((p) => p.isReady);
  const startable = STARTABLE_MODES.includes(room.config.mode);

  const full = room.players.length >= room.config.capacity;
  const canHostStart = isHost && room.players.length >= 2 && startable;
  const hint = !startable
    ? "Mode not playable yet"
    : room.players.length < 2
      ? "Waiting for at least one more player"
      : !full
        ? isHost
          ? "Room not full yet — click Start to launch now, or wait for it to fill up"
          : "Room not full yet — host can launch early"
        : !everyoneReady
          ? "Waiting for everyone to ready up — match starts automatically when all ready"
          : "All ready — starting...";

  target.innerHTML = `
    <div class="panel">
      <div style="display:flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap;">
        <div>
          <h2 class="panel-title" style="margin-bottom: 4px;">${escapeHtml(modeLabel(room.config.mode))}</h2>
          <p class="panel-subtitle">${room.players.length}/${room.config.capacity} players · ${room.config.freePlay ? "Free play" : `Stake ${formatShards(room.config.stake)} · Pot ${formatShards(room.pot)}`}</p>
        </div>
        ${
          room.inviteCode
            ? `<div style="text-align: right;">
                <div style="font-size: 10px; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 1.2px;">Invite code</div>
                <div style="font-family: var(--font-mono); font-size: 18px; font-weight: 700; letter-spacing: 2px;">${escapeHtml(room.inviteCode)}</div>
              </div>`
            : ""
        }
      </div>

      <h3 style="margin-top: 20px; font-size: 13px; letter-spacing: 1px; text-transform: uppercase; color: var(--text-secondary);">Players</h3>
      <div style="display:flex; flex-direction: column; gap: 6px;">
        ${room.players.map((p) => renderPlayerRow(p, room, isHost, user.id)).join("")}
      </div>

      <div class="btn-row spaced" style="margin-top: 22px;">
        <button id="toggle-ready" type="button" class="${me?.isReady ? "" : "primary"}" ${startable ? "" : "disabled"}>
          ${me?.isReady ? "Cancel ready" : "Ready up"}
        </button>
        ${canHostStart ? `<button id="host-start" type="button" class="primary">Start now</button>` : ""}
      </div>
      <p style="text-align: right; font-size: 11px; color: var(--text-secondary); margin: 8px 0 0;">${escapeHtml(hint)}</p>
    </div>
  `;

  target.querySelector<HTMLButtonElement>("#toggle-ready")!.addEventListener("click", () => {
    void toggleReady(room.id, !(me?.isReady ?? false));
  });
  target.querySelector<HTMLButtonElement>("#host-start")?.addEventListener("click", () => {
    void hostStartMatch(room.id);
  });
  target.querySelectorAll<HTMLButtonElement>("button.kick-player").forEach((btn) => {
    btn.addEventListener("click", () => {
      const userId = btn.dataset.userId!;
      void kickPlayer(room.id, userId);
    });
  });
}

function renderPlayerRow(
  player: RoomPlayerSummary,
  room: RoomDetails,
  iAmHost: boolean,
  myUserId: string,
): string {
  const readyTag = player.isHost
    ? `<span class="status-pill status-host">Host</span>`
    : player.isReady
      ? `<span class="status-pill status-ready"><span style="display:inline-flex;vertical-align:middle;margin-right:4px;">${icon("check", 12)}</span>Ready</span>`
      : `<span class="status-pill status-waiting">Not ready</span>`;
  const ratingLabel = player.rating !== null ? `<span style="color: var(--text-secondary); font-size: 11px;">${Math.round(player.rating)} elo</span>` : "";
  const kickBtn =
    iAmHost && player.userId !== myUserId
      ? `<button type="button" class="kick-player" data-user-id="${player.userId}" style="background: transparent; border: 1px solid var(--line-strong); color: var(--text-secondary); padding: 4px 8px; font-size: 11px;">Kick</button>`
      : "";
  return `
    <div class="list-row">
      <div style="display:flex; align-items: center; gap: 10px;">
        ${icon("user", 16)}
        <div style="font-weight: 600;">${escapeHtml(player.username)}</div>
        ${ratingLabel}
      </div>
      <div style="display:flex; align-items:center; gap: 8px;">
        ${readyTag}
        ${kickBtn}
      </div>
    </div>
  `;
}

async function toggleReady(roomId: string, isReady: boolean): Promise<void> {
  try {
    await setLobbyReady(roomId, isReady);
  } catch (err) {
    showToast(extractErrorCode(err, "READY_FAILED"));
  }
}

async function hostStartMatch(roomId: string): Promise<void> {
  try {
    await startLobbyRoom(roomId);
  } catch (err) {
    showToast(extractErrorCode(err, "START_FAILED"));
  }
}

async function kickPlayer(roomId: string, userId: string): Promise<void> {
  try {
    await kickLobbyPlayer(roomId, userId);
  } catch (err) {
    showToast(extractErrorCode(err, "KICK_FAILED"));
  }
}

async function leaveRoom(): Promise<void> {
  try {
    await leaveLobbyRoom();
  } catch {
    // ignore
  }
  stopRoomPolling();
  appState.set({ screen: "room_browser", currentRoomId: null });
}

function stopBrowserPolling(): void {
  if (browserPollHandle !== null) {
    clearInterval(browserPollHandle);
    browserPollHandle = null;
  }
}

function stopRoomPolling(): void {
  if (roomPollHandle !== null) {
    clearInterval(roomPollHandle);
    roomPollHandle = null;
  }
}

function modeLabel(mode: GameMode): string {
  switch (mode) {
    case "arena":
      return "Arena";
    case "coop":
      return "Coop Survival";
    case "boss_hunt":
      return "Boss Hunt";
    case "boss_endurance":
      return "Boss Endurance";
    default:
      return mode;
  }
}

function formatShards(n: number): string {
  if (n === 0) return "0";
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

function showToast(message: string): void {
  const overlay = document.createElement("div");
  overlay.className = "toast";
  overlay.textContent = message;
  document.body.appendChild(overlay);
  setTimeout(() => overlay.remove(), 3000);
}

function extractErrorCode(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.code;
  if (err instanceof Error) return err.message;
  return fallback;
}

function humanError(code: string): string {
  switch (code) {
    case "INSUFFICIENT_SHARDS":
      return "Not enough shards to cover the stake.";
    case "NO_SERVER_AVAILABLE":
      return "No game server available right now.";
    case "GAME_SERVER_UNAVAILABLE":
      return "Failed to reach the game server.";
    case "PLAYERS_NOT_READY":
      return "Some players are not ready yet.";
    case "NOT_ENOUGH_PLAYERS":
      return "Not enough players to start.";
    case "MODE_NOT_PLAYABLE_YET":
      return "This mode is not playable yet.";
    case "ROOM_FULL":
      return "Room is full.";
    case "ALREADY_IN_ROOM":
      return "You are already in another room.";
    case "INVALID_INVITE_CODE":
      return "Invalid invite code.";
    default:
      return code;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}
